import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../../utils/axiosConfig';

// v1.26.0 — Kat karşılığı arsa ortağının notları ve talepleri. Talepler için AI destekli çözüm önerisi alınır;
// öneride bölüm paketi varsa "Daire Öner"de açılabilir.

export interface OneriKalemi { bolumID: number; bolumAdi: string; oran: number; tutar: number; }
interface CozumOnerisi {
    kaynak: 'ai' | 'kural';
    cozum: string;
    adimlar: string[];
    paket: { gerekce: string; toplam: number; kalemler: OneriKalemi[] } | null;
}
interface OrtakNotu {
    notID: number;
    tur: 'NOT' | 'TALEP';
    metin: string;
    durum: 'ACIK' | 'KARSILANDI';
    olusturanAdi: string | null;
    olusturmaTarihi: string;
    cozumTarihi: string | null;
    cozumOnerisi: CozumOnerisi | null;
}

const tarih = (s: string) => new Date(s).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const tl = (n: number) => `${n.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺`;

const OrtakNotlari: React.FC<{
    arsaId: number;
    ortakId: number;
    /** Not / talep eklenince ya da değişince (listelerdeki sayı rozetleri için) */
    onDegisti?: () => void;
    /** Çözüm önerisindeki paketi Daire Öner panelinde aç */
    onPaketAc?: (kalemler: OneriKalemi[]) => void;
    baslangicSekme?: 'NOT' | 'TALEP';
}> = ({ arsaId, ortakId, onDegisti, onPaketAc, baslangicSekme = 'TALEP' }) => {
    const [liste, setListe] = useState<OrtakNotu[] | null>(null);
    const [sekme, setSekme] = useState<'NOT' | 'TALEP'>(baslangicSekme);
    const [yeni, setYeni] = useState('');
    const [kaydediliyor, setKaydediliyor] = useState(false);
    const [duzen, setDuzen] = useState<{ id: number; metin: string } | null>(null);
    const [cozumIslemde, setCozumIslemde] = useState<number | null>(null);
    const taban = `/api/KatKarsiligArsa/${arsaId}/ortaklar/${ortakId}/notlar`;

    const yukle = useCallback(async () => {
        try { setListe((await axiosInstance.get(taban)).data); } catch { setListe([]); }
    }, [taban]);
    useEffect(() => { yukle(); }, [yukle]);

    const guncelle = (n: OrtakNotu) => setListe(l => (l ?? []).map(x => (x.notID === n.notID ? n : x)));

    const ekle = async () => {
        if (!yeni.trim()) return;
        setKaydediliyor(true);
        try {
            const r = await axiosInstance.post(taban, { tur: sekme, metin: yeni.trim() });
            setListe(l => [r.data, ...(l ?? [])]);
            setYeni('');
            onDegisti?.();
        } catch { toast.error('Kaydedilemedi.'); }
        finally { setKaydediliyor(false); }
    };
    const kaydetDuzen = async (n: OrtakNotu) => {
        if (!duzen?.metin.trim()) return;
        try {
            guncelle((await axiosInstance.post(`${taban}/update/${n.notID}`, { tur: n.tur, metin: duzen.metin, durum: n.durum })).data);
            setDuzen(null);
        } catch { toast.error('Kaydedilemedi.'); }
    };
    const durumDegistir = async (n: OrtakNotu) => {
        try {
            guncelle((await axiosInstance.post(`${taban}/update/${n.notID}`, { tur: n.tur, metin: n.metin, durum: n.durum === 'ACIK' ? 'KARSILANDI' : 'ACIK' })).data);
            onDegisti?.();
        } catch { toast.error('Kaydedilemedi.'); }
    };
    const sil = async (n: OrtakNotu) => {
        if (!window.confirm(`${n.tur === 'TALEP' ? 'Talep' : 'Not'} silinsin mi?`)) return;
        try { await axiosInstance.post(`${taban}/delete/${n.notID}`); setListe(l => (l ?? []).filter(x => x.notID !== n.notID)); onDegisti?.(); }
        catch { toast.error('Silinemedi.'); }
    };
    const cozumAl = async (n: OrtakNotu) => {
        setCozumIslemde(n.notID);
        try {
            const r = await axiosInstance.post(`${taban}/${n.notID}/cozum`);
            guncelle({ ...n, cozumOnerisi: r.data, cozumTarihi: new Date().toISOString() });
        } catch { toast.error('Çözüm önerisi alınamadı.'); }
        finally { setCozumIslemde(null); }
    };

    const gorunen = (liste ?? []).filter(n => n.tur === sekme);
    const notSayisi = (liste ?? []).filter(n => n.tur === 'NOT').length;
    const acikTalep = (liste ?? []).filter(n => n.tur === 'TALEP' && n.durum === 'ACIK').length;
    const talepSayisi = (liste ?? []).filter(n => n.tur === 'TALEP').length;

    return (
        <div className="kka-not-kutu">
            <div className="kka-not-sekmeler">
                <button className={sekme === 'TALEP' ? 'aktif' : ''} onClick={() => setSekme('TALEP')}>
                    <i className="fas fa-hand-paper" style={{ marginRight: 6 }}></i>Talepleri
                    <span className={`sayi${acikTalep ? ' acik' : ''}`} title={`${acikTalep} açık / ${talepSayisi} talep`}>{acikTalep ? `${acikTalep} açık` : talepSayisi}</span>
                </button>
                <button className={sekme === 'NOT' ? 'aktif' : ''} onClick={() => setSekme('NOT')}>
                    <i className="fas fa-sticky-note" style={{ marginRight: 6 }}></i>Notlar<span className="sayi">{notSayisi}</span>
                </button>
            </div>
            <div className="kka-not-govde">
                <div className="kka-not-ekle">
                    <textarea value={yeni} onChange={e => setYeni(e.target.value)} rows={2}
                        placeholder={sekme === 'TALEP' ? 'Ortağın talebi — ör. "Giriş katı istemiyor, güney cepheli 3+1 bir daire talep ediyor"' : 'Not ekleyin…'}
                        onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) ekle(); }} />
                    <button className="kka-btn-primary" onClick={ekle} disabled={kaydediliyor || !yeni.trim()} style={{ whiteSpace: 'nowrap' }}>
                        <i className={`fas ${kaydediliyor ? 'fa-spinner fa-spin' : 'fa-plus'}`} style={{ marginRight: 5 }}></i>Ekle
                    </button>
                </div>
                {liste === null && <div className="kka-empty"><i className="fas fa-spinner fa-spin"></i></div>}
                {liste && gorunen.length === 0 && (
                    <div style={{ fontSize: '0.82rem', color: '#94a3b8', padding: '4px 2px' }}>
                        {sekme === 'TALEP' ? 'Henüz talep yok. Ortağın istediklerini (kat, cephe, tip, takas…) yazın; AI ile çözüm önerisi alabilirsiniz.' : 'Henüz not yok.'}
                    </div>
                )}
                {gorunen.map(n => (
                    <div key={n.notID} className={`kka-not-satir${n.tur === 'TALEP' && n.durum === 'KARSILANDI' ? ' karsilandi' : ''}`}>
                        <div className="kka-not-ust">
                            {n.tur === 'TALEP' && <span className={`kka-not-durum ${n.durum}`}>{n.durum === 'ACIK' ? 'Açık' : 'Karşılandı'}</span>}
                            <span>{tarih(n.olusturmaTarihi)}{n.olusturanAdi ? ` · ${n.olusturanAdi}` : ''}</span>
                            <span style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                                {n.tur === 'TALEP' && (
                                    <button className="kka-mini-btn" onClick={() => durumDegistir(n)} title={n.durum === 'ACIK' ? 'Karşılandı olarak işaretle' : 'Yeniden aç'}>
                                        <i className={`fas ${n.durum === 'ACIK' ? 'fa-check' : 'fa-undo'}`}></i>
                                    </button>
                                )}
                                <button className="kka-mini-btn" onClick={() => setDuzen({ id: n.notID, metin: n.metin })} title="Düzenle"><i className="fas fa-pen"></i></button>
                                <button className="kka-mini-btn" onClick={() => sil(n)} title="Sil"><i className="fas fa-trash" style={{ color: '#dc2626' }}></i></button>
                            </span>
                        </div>
                        {duzen?.id === n.notID ? (
                            <div className="kka-not-ekle" style={{ margin: 0 }}>
                                <textarea value={duzen.metin} autoFocus onChange={e => setDuzen({ id: n.notID, metin: e.target.value })} />
                                <button className="kka-mini-btn" onClick={() => kaydetDuzen(n)}><i className="fas fa-check" style={{ color: '#16a34a' }}></i></button>
                                <button className="kka-mini-btn" onClick={() => setDuzen(null)}><i className="fas fa-times"></i></button>
                            </div>
                        ) : <div className="kka-not-metin">{n.metin}</div>}

                        {n.tur === 'TALEP' && (
                            <div style={{ marginTop: 6 }}>
                                <button className="kka-mini-btn mor" onClick={() => cozumAl(n)} disabled={cozumIslemde === n.notID}>
                                    <i className={`fas ${cozumIslemde === n.notID ? 'fa-spinner fa-spin' : 'fa-magic'}`}></i>
                                    {n.cozumOnerisi ? 'Yeniden çözüm öner' : 'AI ile çözüm öner'}
                                </button>
                                {n.cozumOnerisi && (
                                    <div className="kka-cozum">
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                                            <span className={`kka-kaynak${n.cozumOnerisi.kaynak === 'kural' ? ' kural' : ''}`}>
                                                <i className={`fas ${n.cozumOnerisi.kaynak === 'ai' ? 'fa-magic' : 'fa-calculator'}`}></i>
                                                {n.cozumOnerisi.kaynak === 'ai' ? 'AI önerisi' : 'Kural tabanlı'}
                                            </span>
                                            {n.cozumTarihi && <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>{tarih(n.cozumTarihi)}</span>}
                                        </div>
                                        <div style={{ whiteSpace: 'pre-wrap' }}>{n.cozumOnerisi.cozum}</div>
                                        {n.cozumOnerisi.adimlar.length > 0 && <ol>{n.cozumOnerisi.adimlar.map((a, i) => <li key={i}>{a}</li>)}</ol>}
                                        {n.cozumOnerisi.paket && n.cozumOnerisi.paket.kalemler.length > 0 && (
                                            <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                <span style={{ fontWeight: 700 }}>
                                                    Önerilen: {n.cozumOnerisi.paket.kalemler.map(k => `${k.bolumAdi} %${k.oran.toLocaleString('tr-TR', { maximumFractionDigits: 2 })}`).join(' + ')}
                                                    {' '}({tl(n.cozumOnerisi.paket.toplam)})
                                                </span>
                                                {onPaketAc && (
                                                    <button className="kka-mini-btn" onClick={() => onPaketAc(n.cozumOnerisi!.paket!.kalemler)}>
                                                        <i className="fas fa-calculator"></i>Daire Öner'de dene
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default OrtakNotlari;
